import React from "react";
import styled from "styled-components";
import { Plus, Minus, X } from "lucide-react";
import { useLanguage } from "../contexts/language";
import { useOrderActions } from "../contexts/order";
import { unmodule } from "../util";

export const fallbackMenuImage = unmodule(
  require("../assets/hebe-logo-vertical.png"),
);

// A menu photo whose Active Storage blob has gone missing renders as a broken-image glyph, which
// reads as the whole page being broken. Swap in the logo instead, once per element.
export const swapInFallbackImage = (event) => {
  if (event.currentTarget.src === fallbackMenuImage) return;
  event.currentTarget.src = fallbackMenuImage;
};

const ItemContainer = styled.div`
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  transition: box-shadow 0.2s ease;
  opacity: ${(props) =>
    props.$isOutOfStock || props.$isKitchenClosed ? 0.6 : 1};
  pointer-events: ${(props) =>
    props.$isOutOfStock || props.$isKitchenClosed ? "none" : "auto"};

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const ImageWrapper = styled.div`
  width: 88px;
  height: 88px;
  flex-shrink: 0;
  border-radius: 10px;
  overflow: hidden;
  background-color: #f0f0f0;
  cursor: pointer;

  @media (max-width: 719px) {
    width: 72px;
    height: 72px;
  }
`;

const ItemImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
`;

const ItemDescriptionDesktop = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0 0 8px 0;
  line-height: 1.4;

  @media (max-width: 719px) {
    display: none;
  }
`;

const ItemDescriptionMobile = styled.p`
  display: none;
  font-size: 12px;
  color: #666;
  margin: 8px 0 0 0;
  line-height: 1.4;

  @media (max-width: 719px) {
    display: block;
  }
`;

const StockInfo = styled.p`
  font-size: 12px;
  color: ${(props) => (props.$isOutOfStock ? "#999" : "#c9a961")};
  margin: 0;
  font-weight: 500;
`;

const PriceActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
`;

const Price = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #c9a961;

  @media (max-width: 719px) {
    font-size: 14px;
  }
`;

const ActionsWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: #f0f0f0;
  border-radius: 4px;
  padding: 2px;
`;

const QuantityButton = styled.button`
  padding: 8px;
  min-width: 36px;
  min-height: 36px;
  border: none;
  background: none;
  cursor: pointer;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background-color 0.15s;

  @media (max-width: 719px) {
    padding: 6px;
    min-width: 30px;
    min-height: 30px;
  }

  &:hover {
    background-color: #e0e0e0;
  }

  &:active {
    background-color: #d5d5d5;
  }
`;

const QuantityDisplay = styled.span`
  width: 24px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const ZoomModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4;
  padding: 20px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
`;

const ZoomedImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
`;

const DisclaimerText = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  text-align: center;
  margin-top: 12px;
  font-style: italic;
`;

const NoteField = styled.textarea`
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  color: #555;
  font-size: 12px;
  margin-top: 8px;
  min-height: 54px;
  padding: 6px 8px;
  resize: vertical;
  width: 100%;
`;

// Static objects, defined once. An inline literal here is a new identity on every render, which
// defeats the memo on everything below it.
const DISABLED_STEP_STYLE = { opacity: 0.3, cursor: "not-allowed" };
const ENABLED_STEP_STYLE = undefined;

const NOTE_DEBOUNCE_MS = 300;

// `quantity` and `note` arrive as PROPS rather than being dug out of the cart here. Reading them from
// context meant every one of the 90 items re-rendered on any cart change and re-scanned the whole
// cart with a find(); as props, the memo below bails out on all but the line that actually changed.
export const MenuItem = React.memo((props) => {
  const {
    item,
    quantity = 0,
    note = "",
    isOutOfStock = false,
    isKitchenClosed = false,
  } = props;
  const { t } = useLanguage();
  const { updateCartQuantity, updateCartItemNote } = useOrderActions();
  const [showZoom, setShowZoom] = React.useState(false);

  const [localNote, setLocalNote] = React.useState(note);
  const debounceRef = React.useRef(null);
  const pendingRef = React.useRef(null);

  // Typing owns the field; an inbound value is only adopted while no edit is in flight, so the
  // debounce cannot clobber what the guest is typing.
  React.useEffect(() => {
    if (!debounceRef.current) setLocalNote(note);
  }, [note]);

  // Held in a ref so the cleanup below can stay on `[]` deps and still see the current props.
  const flushRef = React.useRef(null);
  flushRef.current = () => {
    if (pendingRef.current === null) return;
    const value = pendingRef.current;
    pendingRef.current = null;
    updateCartItemNote(item.id, value);
  };

  // Searching or switching category unmounts the card. Cancelling the timer without committing
  // would drop a note the guest had already finished typing, so flush it on the way out.
  React.useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      flushRef.current();
    },
    [],
  );

  const increaseQuantity = React.useCallback(() => {
    if (quantity >= item.stock) return;
    updateCartQuantity(item.id, quantity + 1, item);
  }, [quantity, item, updateCartQuantity]);

  const decreaseQuantity = React.useCallback(() => {
    updateCartQuantity(item.id, Math.max(0, quantity - 1));
  }, [quantity, item.id, updateCartQuantity]);

  const handleNoteChange = React.useCallback(
    (event) => {
      const value = event.target.value;
      setLocalNote(value);
      pendingRef.current = value;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        pendingRef.current = null;
        updateCartItemNote(item.id, value);
      }, NOTE_DEBOUNCE_MS);
    },
    [item.id, updateCartItemNote],
  );

  // The cart drawer edits this same note through its own buffer, and the card stays mounted behind
  // it. Committing on blur means the pending value is already in the cart before the drawer can seed
  // a row from it, so the two buffers can never hold competing edits of one line.
  const commitNote = React.useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    flushRef.current();
  }, []);

  const openZoom = React.useCallback(() => setShowZoom(true), []);
  const closeZoom = React.useCallback(() => setShowZoom(false), []);

  return (
    <>
      <ItemContainer
        $isOutOfStock={isOutOfStock}
        $isKitchenClosed={isKitchenClosed}
      >
        <TopRow>
          <ImageWrapper onClick={openZoom}>
            {/* No onError here on purpose: the fallback is a 1916x2199 PNG, so a storage outage
                would swap all 90 cards to it at once. The cart drawer, which shows a handful of
                rows, can afford it. */}
            <ItemImage
              src={item.image || fallbackMenuImage}
              alt={item.name}
              loading="lazy"
            />
          </ImageWrapper>

          <ItemInfo>
            <ItemName>{item.name}</ItemName>
            <ItemDescriptionDesktop>{item.description}</ItemDescriptionDesktop>
            <StockInfo $isOutOfStock={isOutOfStock}>
              {isOutOfStock
                ? t.order.outOfStock
                : `${item.stock} ${t.order.available}`}
            </StockInfo>
            {!isOutOfStock && !isKitchenClosed && quantity > 0 && (
              <NoteField
                value={localNote}
                onChange={handleNoteChange}
                onBlur={commitNote}
                maxLength={200}
                placeholder={t.order.itemNotePlaceholder}
              />
            )}
          </ItemInfo>

          <PriceActions>
            <Price>{item?.price?.formattedText || "Rp0"}</Price>

            {!isOutOfStock && !isKitchenClosed && (
              <ActionsWrapper>
                <QuantityControl>
                  <QuantityButton onClick={decreaseQuantity}>
                    <Minus size={14} />
                  </QuantityButton>
                  <QuantityDisplay>{quantity}</QuantityDisplay>
                  <QuantityButton
                    onClick={increaseQuantity}
                    style={
                      quantity >= item.stock
                        ? DISABLED_STEP_STYLE
                        : ENABLED_STEP_STYLE
                    }
                  >
                    <Plus size={14} />
                  </QuantityButton>
                </QuantityControl>
              </ActionsWrapper>
            )}
          </PriceActions>
        </TopRow>

        <ItemDescriptionMobile>{item.description}</ItemDescriptionMobile>
      </ItemContainer>

      {showZoom && (
        <ZoomModal onClick={closeZoom}>
          <CloseButton onClick={closeZoom}>
            <X size={24} />
          </CloseButton>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ textAlign: "center" }}
          >
            <ZoomedImage
              src={item.image || fallbackMenuImage}
              alt={item.name}
              onError={swapInFallbackImage}
            />
            <DisclaimerText>
              *Gambar hanya ilustrasi, bukan foto asli produk
            </DisclaimerText>
          </div>
        </ZoomModal>
      )}
    </>
  );
});
MenuItem.displayName = "MenuItem";
